import React, { useEffect, useMemo, useRef } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Container } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import routes from "../../routes";
import SurveyModal from "../../commons/components/SurveyModal";
import { refreshBasicData } from "../../commons/services/store/commons.actions";
import LoadingSpinner from "../../_UI/Components/LoadingSpinner";
import { ErrorMessage } from "../../_UI/Components/Alerts/PathwayAlertMessages";
import { getResponseNotification } from "../../notification/store/notification.actions";
import NotificationModal from "../../notification/components/NotificationModal";
import { selectOrganization, sendCelloRegistrationData } from "../../user/services/store/user.actions";
import NavSidebar from "./NavSidebar";
import styled from "styled-components";
import { DefaultHeader } from "./DefaultHeader";
import Breadcrumbs from "./Breadcrumbs";
import useCelloAttribution from "../../_hooks/useCelloAttribution";
import useUserAuthentication from "../../_hooks/useUserAuthentication";
import { OrganizationType } from "../../_statics/OrganizationType";
import { OrganizationContext } from "../../integrations/_commons/PlatformContext";
import { setupActions } from "../../user/services/store/setup.actions";
import { adminAreaActions } from "../../admin/services/store/adminarea.actions";

const DefaultLayout = () => {
    const commons = useSelector((state) => state.commons);
    const authentication = useSelector((state) => state.authentication);

    const user = useSelector((state) => state.user);
    const selectedOrganization = useSelector((state) =>
        state.authentication.user ? state.authentication.user.selectedOrganization : undefined
    );

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const { ucc } = useCelloAttribution();
    const { isSuperUser } = useUserAuthentication();

    const hasCelloRun = useRef(false);

    useEffect(() => {
        if (hasCelloRun.current) return;
        if (ucc && authentication.user.externalUUID && selectedOrganization?.externalUUID && !isSuperUser) {
            dispatch(sendCelloRegistrationData(ucc, authentication.user));
            hasCelloRun.current = true;
        }
    }, [ucc, dispatch, isSuperUser, authentication.user, authentication.user?.externalUUID, selectedOrganization]);

    useEffect(() => {
        if (location && location.search && new URLSearchParams(location.search).has("organization")) {
            dispatch(
                selectOrganization(new URLSearchParams(location.search).get("organization"), true, location.pathname)
            );
        } else {
            dispatch(refreshBasicData());
            dispatch(setupActions.getSetupStatus());
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isSuperUser) {
            dispatch(adminAreaActions.getActiveMilestones());
            dispatch(adminAreaActions.getProjectLabels());
        }
    }, [isSuperUser, dispatch]);

    useEffect(() => {
        if (location && location.search && new URLSearchParams(location.search).has("notificationId")) {
            dispatch(getResponseNotification(new URLSearchParams(location.search).get("notificationId")));
        }
    }, [dispatch, location]);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const notificationId = searchParams.get("notificationId");

        if (
            selectedOrganization &&
            selectedOrganization.organizationType === OrganizationType.LEX_OFFICE &&
            !location.pathname.startsWith("/lexoffice")
        ) {
            navigate("/lexoffice");
        }
        if (
            selectedOrganization &&
            authentication.error &&
            selectedOrganization.error === "User has no Organization selected."
        ) {
            const loginUrl = notificationId ? `/login?notificationId=${notificationId}` : "/login";
            navigate(loginUrl);
        }
    }, [selectedOrganization, authentication, navigate, location]);

    const isLoading = useMemo(() => {
        return !!(commons.basicDataLoading || user.switchOrganizationLoading);
    }, [commons.basicDataLoading, user.switchOrganizationLoading]);

    return (
        <div>
            <OrganizationContext.Provider value={selectedOrganization}>
                <NotificationModal />

                <SurveyModal />

                <DefaultHeader />

                <AppBody className="app-body" style={{ height: "100%" }}>
                    <NavSidebar />
                    {/*
                    <AppSidebar display="lg">
                    {selectedOrganization &&
                        selectedOrganization.allowedApps &&
                        selectedOrganization.allowedModules && (
                            <AppSidebarNav navConfig={navigation} style={{ position: "fixed", height: "90%" }} />
                        )}
                </AppSidebar>
                     */}

                    <main className="main" style={{ minHeight: "100% !important" }}>
                        <Breadcrumbs />

                        <Container>
                            {isLoading ? (
                                <LoadingSpinner loading={isLoading} size="4rem" />
                            ) : commons.basicDataErrorMessage ? (
                                <ErrorMessage marginless>{commons.basicDataErrorMessage}</ErrorMessage>
                            ) : (
                                <Routes>
                                    {routes.map((route, idx) => {
                                        return route.component ? (
                                            <Route
                                                key={idx}
                                                path={route.path}
                                                exact={!!route.exact}
                                                name={route.name}
                                                element={
                                                    route.superUserOnly && !isSuperUser ? (
                                                        <Navigate to="/" replace />
                                                    ) : (
                                                        <route.component />
                                                    )
                                                }
                                            />
                                        ) : null;
                                    })}
                                </Routes>
                            )}
                        </Container>
                    </main>
                </AppBody>
            </OrganizationContext.Provider>
        </div>
    );
};

export default DefaultLayout;

const AppBody = styled.div`
    display: grid;
    grid-template-columns: max-content auto;

    background: #eff8fa;
    min-height: 100vh;
    min-width: 100vw;
`;
